import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  EMPTY_ROW_SELECTION,
  moveRowSelection,
  type RowSelection,
  reconcileRowSelection,
  rowSelectMode,
  selectAllRows,
  selectRow,
} from './rowSelection';

/**
 * WHO OWNS THE SELECTION, and why it is not the table.
 *
 * The selection is what the DETAIL PANE is about, and the detail pane is the table's
 * sibling rather than its child (spec/screens/list.md section 2 item 5). So the state sits
 * above both, on the list page, and the table publishes the ORDER it is currently showing
 * into it - because "extend from here to there" and "the next row down" are questions about
 * the order on screen, which only the table knows.
 *
 * IT WRITES NOTHING AND IT IS NOT IN THE URL. A selection is where a person's attention is
 * for the next few seconds; a view is what they send somebody. `ShellTableSelection` next
 * door already made that choice for the shell's own single-row case, and this is the same
 * choice for the list's real one.
 */
export interface RowSelectionApi {
  readonly selection: RowSelection;
  readonly isSelected: (recordId: string) => boolean;
  /** One click on a row, with the modifiers the pointer carried. */
  readonly click: (
    recordId: string,
    event: { readonly shiftKey: boolean; readonly metaKey: boolean; readonly ctrlKey: boolean },
  ) => void;
  /**
   * A secondary click acts on WHAT IS SELECTED when the row is part of it, and selects the
   * row otherwise - the macOS rule, and the reason a context menu on one of three selected
   * rows archives three rather than one.
   */
  readonly contextClick: (recordId: string) => void;
  readonly clear: () => void;
  readonly selectAll: () => void;
  readonly move: (delta: number, extend: boolean) => void;
  /** The table calls this with the ids it is showing, in the order it is showing them. */
  readonly setOrder: (order: readonly string[]) => void;
}

const NO_SELECTION: RowSelectionApi = {
  selection: EMPTY_ROW_SELECTION,
  isSelected: () => false,
  click: () => undefined,
  contextClick: () => undefined,
  clear: () => undefined,
  selectAll: () => undefined,
  move: () => undefined,
  setOrder: () => undefined,
};

const RowSelectionContext = createContext<RowSelectionApi>(NO_SELECTION);

export function useRowSelection(): RowSelectionApi {
  return useContext(RowSelectionContext);
}

export function RowSelectionProvider({ children }: { readonly children: ReactNode }) {
  const [selection, setSelection] = useState<RowSelection>(EMPTY_ROW_SELECTION);
  /**
   * THE ORDER IS A REF AND A STATE AT ONCE, and both are needed. The ref is what the
   * handlers read - a click must resolve against the rows on screen at the moment of the
   * click, not against whatever the closure captured - and the state is what re-runs the
   * reconciliation when the list changes under a standing selection.
   */
  const orderRef = useRef<readonly string[]>([]);
  const [order, setOrderState] = useState<readonly string[]>([]);

  const setOrder = useCallback((next: readonly string[]) => {
    orderRef.current = next;
    setOrderState((current) =>
      current.length === next.length && current.every((id, index) => id === next[index])
        ? current
        : next,
    );
  }, []);

  useEffect(() => {
    setSelection((current) => reconcileRowSelection(current, order));
  }, [order]);

  const api = useMemo<RowSelectionApi>(
    () => ({
      selection,
      isSelected: (recordId) => selection.ids.includes(recordId),
      click: (recordId, event) =>
        setSelection((current) =>
          selectRow(current, orderRef.current, recordId, rowSelectMode(event)),
        ),
      contextClick: (recordId) =>
        setSelection((current) =>
          current.ids.includes(recordId)
            ? current
            : selectRow(current, orderRef.current, recordId, 'replace'),
        ),
      clear: () => setSelection(EMPTY_ROW_SELECTION),
      selectAll: () => setSelection(selectAllRows(orderRef.current)),
      move: (delta, extend) => {
        /**
         * COMPUTED HERE RATHER THAN INSIDE THE SETTER, because the row that gains the
         * selection also has to gain the FOCUS: the emphasized fill is `table:focus-within`
         * and the key press landed on the scroll region, which is the table's PARENT - so
         * without this an arrow key paints the unemphasized grey while the reader is
         * standing in the table. Focusing the row also scrolls it into view, which is what
         * an arrow key at the bottom of a page has to do anyway.
         */
        const next = moveRowSelection(selection, orderRef.current, delta, extend);
        setSelection(next);
        const landed = delta < 0 ? next.ids[0] : next.ids.at(-1);
        if (landed === undefined) return;
        requestAnimationFrame(() => {
          document
            .querySelector<HTMLElement>(`[data-row-id="${CSS.escape(landed)}"]`)
            ?.focus({ preventScroll: false });
        });
      },
      setOrder,
    }),
    [selection, setOrder],
  );

  return <RowSelectionContext value={api}>{children}</RowSelectionContext>;
}
